import { css, html, nothing } from "lit";
import { connect } from "pwa-helpers";
import { BaseComponent } from "../base-component/base-component";
import store from "../../store/store";
import emit from '../../common/events';
import { UserProfile } from "../user-profile/user-profile.interface";
import { StoreState } from "../../store/store.interface";
import { getCartPageUrl, addToCartUrlContext, isHEInstructor } from "../../common/utils/cart-util";
import { getCartContext } from "../cart/shop-cart/store/shop-cart.selectors";
import { updateInstructorData } from "../instructor-sample";
import { getUserDataForUserAnalytics, selectUser } from "../user-profile/user-profile.selectors";
import { HeInstructorData } from "../instructor-sample/instructor-sample.interface";
import { customElement, property, state } from "lit/decorators.js";
import { getShopCart } from "../cart/shop-cart/thunks/get-shop-cart.thunk";
import { addToCart } from "../cart/shop-cart/thunks/add-to-cart.thunk";
import { showConfirmInstructorModal } from './utils';
import { addGuestInstructorSampleToCart } from "../cart/shop-cart/thunks/add-guest-instructor-sample-to-cart.thunk";
import environment from "../../common/environment";

export enum ButtonType {
    PrintSample = "print",
    EbookSample = "ebook",
    CustomLink = "custom-link",
}

export enum IconPosition {
    Left = "left",
    Right = "right",
}

interface ButtonConfig {
    type: ButtonType;
    label: string;
    link?: string;
    id?: string;
    openInNewTab?: boolean;
    icon?: string;
    iconPosition?: IconPosition;
    iconSize: string;
    textAlignment: string;
    tag? : string;
    comp? : string;
    name?: string;
    subarea?: string;
}

@customElement('mhecomm-card-buttons')
export class CardButtons extends connect(store)(BaseComponent) {

    static styles = [css`
        .uxt-mhcorp, .button-container { 
            height:100% 
        }
    `]
    /**
     * Need to know this to add item to the cart.
     */
    @property()
    businessUnit = "HE";

    @property()
    loginViewLink = "";

    /**
     * isbn of free print sample. Empty string if no sample
     */
    @property()
    isOutOfStock = "";

    /**
     * isbn of free ebook sample. Empty string if no sample
     */
    @property()
    eBookSampleId = "";
    /**
     * isbn of free print sample. Empty string if no sample
     */
    @property()
    printSampleId = "";

    /**
     * Offer pricing identifier for instructor sample requests.
     */
    @property()
    offerPricingId = "";

    @property()
    store: string | undefined = undefined;

    @property()
    buttonString = "";

    @state()
    private isGuestOrHeInstructor = true;

    private user?: UserProfile;

    connectedCallback() {
        super.connectedCallback();
        store.dispatch(getShopCart({ store: this.store }));
    }

    stateChanged(state: StoreState) {
        this.user = selectUser(state);
        this.isGuestOrHeInstructor = !this.user || isHEInstructor(this.user);
    }

    private dataLayerDetail() {
        return {
            businessUnit: this.businessUnit,
            eBookSampleId: this.eBookSampleId,
            printSampleId: this.printSampleId,
            isOutOfStock: this.isOutOfStock,
        };
    }

    /**
     * Return ISBN based on clicked item .
     */
    private getISBN(type: ButtonType): string {
        if (type === ButtonType.EbookSample) {
            return this.eBookSampleId;
        } else if (type === ButtonType.PrintSample) {
            return this.printSampleId;
        } else {
            return "";
        }
    }

    public handleRedirect(type: ButtonType, link?: string, guestCartId?: string) {
        this.toggleLoader();
        let redirectUrl: string = link || (this.user ? getCartPageUrl(this.user) : `${this.loginViewLink + this.getISBN(type)}`);
        if (guestCartId) {
            redirectUrl = this.appendGuestCartId(redirectUrl, guestCartId);
        }
        window.location.href = redirectUrl;
        // need for unit test purpose

        return redirectUrl;
    }

    private appendGuestCartId(redirectUrl: string, guestCartId: string) {
        const url = new URL(redirectUrl);
        url.searchParams.set('magentoCart', environment().checkoutFlow);
        url.searchParams.set('guestcartid', guestCartId);
        return url.toString();
    }

    updateSampleData(id: string, type: string) {
        const instructorData: HeInstructorData = {
            isbn: id,
            sampleType: type
        }
        return instructorData;
    }

    toggleLoader() {
        const slotNodes = this.shadowRoot?.querySelector('slot')?.assignedNodes();
        const slotCardButton = slotNodes?.find((node: Node) => node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).classList.contains('card-button'));
        if(slotCardButton) {
            (slotCardButton as HTMLElement).classList.toggle('loading');
        }
    }

    emitRequestSampleClickEvent(type: ButtonType) {
        let format = '';
        if (type === ButtonType.EbookSample) {
            format = 'eBook';
        } else if (type === ButtonType.PrintSample) {
            format = 'Print';
        }
        // this unconventional event name for backward compatibility
        const eventName = `HE Instr ${format} Sample Pop Up Request Button Clicks`;
        emit({
            element: this,
            name: eventName,
            detail: this.dataLayerDetail(),
            dataLayer: true,
        });
    }

    async onClickRequestSample(type: ButtonType, id: string) {
        this.emitRequestSampleClickEvent(type);

        if(!this.user && !await showConfirmInstructorModal()){
            return;
        }

        this.toggleLoader();

        store.dispatch(updateInstructorData(this.updateSampleData(id, type)));

        emit({
            element: this,
            name: `free-copy-${type.toLowerCase()}-add`,
            detail: this.dataLayerDetail(),
            dataLayer: true
        });

        if (this.user && isHEInstructor(this.user)) {
            try {
                await store.dispatch(addToCart(addToCartUrlContext(getCartContext(store.getState() as StoreState, this.store), {
                    businessUnit: this.businessUnit,
                    isbn: id,
                    quantity: 1,
                    offerPricingId: this.offerPricingId,
                    instructorSampling: true,
                }))).unwrap();
                this.handleRedirect(type);
            } catch (error) {
                this.toggleLoader();
                console.error('Failed to add instructor sample to Magento cart', error);
            }
            return;
        }

        try {
            const guestCartId = await store.dispatch(addGuestInstructorSampleToCart(addToCartUrlContext(getCartContext(store.getState() as StoreState, this.store), {
                businessUnit: this.businessUnit,
                isbn: id,
                quantity: 1,
                offerPricingId: this.offerPricingId,
            }))).unwrap();
            this.handleRedirect(type, undefined, guestCartId);
        } catch (error) {
            this.toggleLoader();
            console.error('Failed to add guest instructor sample to Magento cart', error);
        }
    }

    onClickCustomLink({label, id, comp, tag, name, subarea}: ButtonConfig) {
        emit({
            element: this,
            name: 'click',
            detail: {
                label: label,
                comp: comp ?? 'button',
                id: id,
                user: getUserDataForUserAnalytics(store.getState() as StoreState),
                tag,
                eventName: name,
                subarea
            },
            dataLayer: true
        });
    }

    handleOnclick(button: ButtonConfig) {
        if (button.type === ButtonType.PrintSample && this.printSampleId) {
            this.onClickRequestSample(ButtonType.PrintSample, this.printSampleId);
        } else if (button.type === ButtonType.EbookSample && this.eBookSampleId) {
            this.onClickRequestSample(ButtonType.EbookSample, this.eBookSampleId);
        } else if (button.type === ButtonType.CustomLink && button.link) {
            this.onClickCustomLink(button);
        }
    }

    renderComp() {
        if(!this.buttonString) {
            return nothing;
        }

        const button = JSON.parse(this.buttonString);
        return this.isGuestOrHeInstructor ? html`
            <div class="button-container" @click="${() => this.handleOnclick(button)}">
                <slot></slot>
            </div>
        ` : nothing;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'mhecomm-card-buttons': CardButtons;
    }
}
